import type { ProviderOperationsAction, ProviderOperationsSearchRowResponse } from "libs/apiClient";
import { getProviderOperationsRowKey } from "./getProviderOperationsRowKey";
import { isGameScopedAction } from "./isGameScopedAction";

export const PROVIDER_OPERATIONS_BULK_LIMIT = 10;

export type ProviderOperationsBulkPlan = {
	rowsToRun: ProviderOperationsSearchRowResponse[];
	/** Rows that can't run: the action isn't available, or another row of the same game runs a game-scoped action. */
	skippedCount: number;
};

function getEligibleRows(
	rows: ProviderOperationsSearchRowResponse[],
	action: ProviderOperationsAction,
): ProviderOperationsSearchRowResponse[] {
	const rowsWithAction = rows.filter((row) => row.availableActions.includes(action));
	if (isGameScopedAction(action) === false) {
		return rowsWithAction;
	}

	const selectedGames = new Set<string>();

	return rowsWithAction.filter((row) => {
		const gameKey = `${row.provider}:${row.gameId}`;
		if (selectedGames.has(gameKey)) {
			return false;
		}

		selectedGames.add(gameKey);

		return true;
	});
}

export function getProviderOperationsBulkPlan(
	rows: ProviderOperationsSearchRowResponse[],
	action: ProviderOperationsAction,
	alreadyRunRowKeys: ReadonlySet<string> = new Set(),
): ProviderOperationsBulkPlan {
	const eligibleRows = getEligibleRows(rows, action);
	const eligibleRowKeys = new Set(eligibleRows.map(getProviderOperationsRowKey));
	const rowsNotRunYet = eligibleRows.filter((row) => alreadyRunRowKeys.has(getProviderOperationsRowKey(row)) === false);
	const skippedRows = rows.filter((row) => {
		const rowKey = getProviderOperationsRowKey(row);

		return eligibleRowKeys.has(rowKey) === false && alreadyRunRowKeys.has(rowKey) === false;
	});

	return {
		rowsToRun: rowsNotRunYet.slice(0, PROVIDER_OPERATIONS_BULK_LIMIT),
		skippedCount: skippedRows.length,
	};
}
